import { commercialPropertyTypes, PropertyPurpose } from '@/helpers/const';

import type {
  ApplicantAsset,
  ApplicantLiability,
  AssetAccount,
  CompletedLoanApplication,
  CreditCardLiability,
  DescriptionAsset,
  Employment,
  IncomingProperty,
  LoanApplication,
  MotorVehicleAsset,
  OtherLiability,
  OutgoingProperty,
  PropertyAsset,
  SalaryEmployment,
  SelfEmployment,
  SupportingProperty,
  VehicleLiability,
} from '@/types';
export const isLoanAppComplete = (loanApp: LoanApplication): loanApp is CompletedLoanApplication => {
  if (!loanApp.userType) {
    console.error('User type is missing - userType', loanApp.userType);
    return false;
  }
  if (!loanApp.loanType) {
    console.error('Loan type is missing - loanType', loanApp.loanType);
    return false;
  }
  if (loanApp.lendingProduct !== 'erhl' && !loanApp.loanDetails) {
    console.error('Loan details are missing = loanDetail', loanApp.loanDetails);
    return false;
  }
  return true;
};

export const isUnemployed = (e: Employment): e is SelfEmployment => e.type === 'unemployed' || e.type === 'retired';
export const isSelfEmployed = (e: Employment): e is SelfEmployment => e.type === 'self_employed';
export const isSalaried = (e: Employment): e is SalaryEmployment =>
  e.type === 'full_time' || e.type === 'part_time' || e.type === 'casual';
export const isPropertyAsset = (a: ApplicantAsset): a is PropertyAsset => a.type === 'property';
export const isOtherAsset = (a: ApplicantAsset): a is AssetAccount =>
  a.type === 'savings' || a.type === 'shares' || a.type === 'superannuation' || a.type === 'gifted_funds';
export const isMotorVehicleAsset = (a: ApplicantAsset): a is MotorVehicleAsset => a.type === 'motor_vehicles';
export const isDescriptionAsset = (a: ApplicantAsset): a is DescriptionAsset =>
  a.type === 'machinery' || a.type === 'equipment';

export const isCreditCardLiability = (l: ApplicantLiability): l is CreditCardLiability => l.type === 'card';
export const isVehicleLiability = (l: ApplicantLiability): l is VehicleLiability => l.type === 'vehicle';
export const isOtherLiability = (l: ApplicantLiability): l is OtherLiability => l.type === 'other';

/**
 * Checks if the loan application has any assets across all applicants
 * @param loanApp - The loan application to check
 * @returns True if any applicant has assets, false otherwise
 */
export const hasAssets = (loanApp: LoanApplication): boolean => {
  return Object.values(loanApp.financials).some((applicant) => applicant?.assets?.length > 0);
};

const isPropertyCommercial = (p: { propertyType?: string; commercialType?: string }): boolean =>
  !!p.commercialType && commercialPropertyTypes.has(p.commercialType);

/**
 * Returns true if any property (incoming, outgoing, supporting) indicates commercial per product matrix.
 * Used to hide "Individuals" option in Borrower Type when property details flag as commercial.
 */
export const isCommercialFromPropertyDetails = (data: LoanApplication): boolean => {
  const check = (props: Array<IncomingProperty | OutgoingProperty | SupportingProperty>) =>
    props.some(isPropertyCommercial);

  return (
    check(data.incomingProperties ?? []) ||
    check(data.outgoingProperties ?? []) ||
    check(data.supportingProperties ?? [])
  );
};

const allAssets = (loanApp: LoanApplication): ApplicantAsset[] => [
  ...Object.values(loanApp.financials).flatMap((applicant) => applicant?.assets ?? []),
  ...Object.values(loanApp.entityAssets ?? {}).flat(),
];

/**
 * Returns true if the loan application has a property (security or non-security) matching the
 * given purpose, either as an incoming/supporting property from Property Details, or as a
 * property asset from Assets & Liabilities (across individual and entity financials).
 */
const hasPropertyWithPurpose = (
  loanApp: LoanApplication,
  purpose: (typeof PropertyPurpose)[keyof typeof PropertyPurpose],
): boolean => {
  const matchesPurpose = (propertyPurpose?: string) => propertyPurpose === purpose;

  const hasPropertyDetailsMatch = [...(loanApp.incomingProperties ?? []), ...(loanApp.supportingProperties ?? [])].some(
    (property) => matchesPurpose(property.propertyPurpose),
  );

  const hasAssetMatch = allAssets(loanApp)
    .filter(isPropertyAsset)
    .some((asset) => matchesPurpose(asset.propertyPurpose));

  return hasPropertyDetailsMatch || hasAssetMatch;
};

/**
 * Returns true if the loan application has an owner-occupied property, either as a security
 * property (incoming/supporting property from Property Details) or as a non-security asset
 * (from Assets & Liabilities, across individual and entity financials).
 */
export const hasOwnerOccupiedProperty = (loanApp: LoanApplication): boolean =>
  hasPropertyWithPurpose(loanApp, PropertyPurpose.OWNER_OCCUPIED);

/**
 * Returns true if the loan application has an investment property, either as a security
 * property (incoming/supporting property from Property Details) or as a non-security asset
 * (from Assets & Liabilities, across individual and entity financials).
 */
export const hasInvestmentProperty = (loanApp: LoanApplication): boolean =>
  hasPropertyWithPurpose(loanApp, PropertyPurpose.INVESTMENT_PROPERTY);

/**
 * Returns true if the loan application has a superannuation asset listed in Assets & Liabilities,
 * across individual and entity financials.
 */
export const hasSuperannuationAsset = (loanApp: LoanApplication): boolean =>
  allAssets(loanApp).some((asset) => asset.type === 'superannuation');

/**
 * Returns true if the loan application has any assets listed in Assets & Liabilities, across
 * individual and entity financials.
 */
export const hasAnyAssets = (loanApp: LoanApplication): boolean => allAssets(loanApp).length > 0;
